import { Uuid } from '../../models/Base';
import { Game } from '../../models/GameStore';
import { Cursor } from '../../models/Repository';
import {
  Album,
  AlbumListField,
  AlbumListType,
  Leaderboard,
  MultiplayerGame,
  MultiplayerGameSession,
  PerformanceGame,
  PerformanceGameListField,
  Space,
  SpaceMembership,
  SpaceMembershipMapType,
  SpaceMembershipTier,
  SpaceMembershipTierInventory,
  SpaceMembershipTierMapType,
  SpaceMembershipTierSubscription,
  SpaceStoreState,
  SpaceTeam,
} from '../../models/SpaceStore';
import { NonEmptyArray } from '../../models/Utils';
import { array } from '../../utils/array';

export const getSpace = (state: SpaceStoreState, classroomId: Uuid) =>
  state.spaces.get(classroomId);

export const getSpaces = (state: SpaceStoreState, fieldName: string) =>
  state.spaces.list(fieldName, null)?.get() ?? [];

export const setSpaces = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...spaces: Space[]
) => {
  state.spaces.lists(array(fieldNames) as NonEmptyArray<string>).set(...spaces);
};

export const pushSpaces = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...spaces: Space[]
) => {
  state.spaces
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .push(...spaces);
};

export const setSpaceCursor = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  cursor: Cursor,
) => {
  state.spaces
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .setCursor(cursor);
};

export const getSpaceMembershipTierInventory = (
  state: SpaceStoreState,
  classroomId: Uuid,
  tierId: Uuid,
) => state.spaceMembershipTierInventories.get(`${classroomId}_${tierId}`);

export const getSpaceMembershipTierInventories = (
  state: SpaceStoreState,
  fieldName: string,
) => state.spaceMembershipTierInventories.list(fieldName, null)?.get() ?? [];

export const setSpaceMembershipTierInventories = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...tierInventories: SpaceMembershipTierInventory[]
) => {
  state.spaceMembershipTierInventories
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...tierInventories);
};

export const pushSpaceMembershipTierInventories = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...tierInventories: SpaceMembershipTierInventory[]
) => {
  state.spaceMembershipTierInventories
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .push(...tierInventories);
};

export const setSpaceMembershipTierInventoryCursor = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  cursor: Cursor,
) => {
  state.spaceMembershipTierInventories
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .setCursor(cursor);
};

export const getSpaceMembershipTierSubscription = (
  state: SpaceStoreState,
  classroomId: Uuid,
  tierId: Uuid,
) => state.spaceMembershipTierSubscriptions.get(`${classroomId}_${tierId}`);

export const getSpaceMembershipTierSubscriptions = (
  state: SpaceStoreState,
  fieldName: string,
) => state.spaceMembershipTierSubscriptions.list(fieldName, null)?.get() ?? [];

export const setSpaceMembershipTierSubscriptions = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...tierSubscriptions: SpaceMembershipTierSubscription[]
) => {
  state.spaceMembershipTierSubscriptions
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...tierSubscriptions);
};

export const pushSpaceMembershipTierSubscriptions = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...tierSubscriptions: SpaceMembershipTierSubscription[]
) => {
  state.spaceMembershipTierSubscriptions
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .push(...tierSubscriptions);
};

export const setSpaceMembershipTierSubscriptionCursor = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  cursor: Cursor,
) => {
  state.spaceMembershipTierSubscriptions
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .setCursor(cursor);
};

export const getSpaceMemebershipTier = (
  state: SpaceStoreState,
  classroomId: Uuid,
  tierId: Uuid,
) => state.spaceMembershipTiers.get(`${classroomId}_${tierId}`);

export const addSpaceMemebershipTierToMap = (
  state: SpaceStoreState,
  classroomId: Uuid,
  tierId: Uuid,
  tier: SpaceMembershipTier | null,
) =>
  state.spaceMembershipTiers
    .map(SpaceMembershipTierMapType.ActiveUser)
    .set(`${classroomId}_${tierId}`, tier);

export const removeSpaceMemebershipTier = (
  state: SpaceStoreState,
  classroomId: Uuid,
  tierId: Uuid,
) => state.spaceMembershipTiers.delete(`${classroomId}_${tierId}`);

export const deleteSpaceMemebershipTierMapItem = (
  state: SpaceStoreState,
  fieldName: SpaceMembershipTierMapType,
  id: string,
) => {
  state.spaceMembershipTiers.map(fieldName).delete(id);
};

export const setActiveUserMemberships = (
  state: SpaceStoreState,
  spaceMemberships: Record<Uuid, SpaceMembership | null>,
) => {
  const field = state.spaceMemberships.map(SpaceMembershipMapType.ActiveUser);
  Object.entries(spaceMemberships).forEach(([spaceId, spaceMembership]) =>
    field.set(spaceId, spaceMembership),
  );
};

export const getSpaceMembership = (
  state: SpaceStoreState,
  classroomId: Uuid,
  userId: string,
) => state.spaceMemberships.get(`${classroomId}_${userId}`);

export const getActiveUserMembership = (
  state: SpaceStoreState,
  classroomId: Uuid,
) =>
  state.spaceMemberships
    .map(SpaceMembershipMapType.ActiveUser)
    .get(classroomId);

export const setMemberships = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...memberships: SpaceMembership[]
) => {
  state.spaceMemberships
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...memberships);
};

export const pushMemberships = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...memberships: SpaceMembership[]
) => {
  state.spaceMemberships
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .push(...memberships);
};

export const getTeam = (state: SpaceStoreState, teamId: Uuid) =>
  state.teams.get(teamId);
export const setTeams = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...teams: SpaceTeam[]
) => {
  state.teams.lists(array(fieldNames) as NonEmptyArray<string>).set(...teams);
};

export const getLeaderboard = (state: SpaceStoreState, leaderboardId: Uuid) =>
  state.leaderboards.get(leaderboardId);

export const setLeaderboards = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...leaderboards: Leaderboard[]
) => {
  state.leaderboards
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...leaderboards);
};

export const getMultiplayerGame = (
  state: SpaceStoreState,
  multiplayerGameId: Uuid,
) => state.multiplayerGames.get(multiplayerGameId);
export const setMultiplayerGames = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...multiplayerGames: MultiplayerGame[]
) => {
  state.multiplayerGames
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...multiplayerGames);
};

export const getMultiplayerGameSession = (
  state: SpaceStoreState,
  multiplayerGameSessionId: Uuid,
) => state.multiplayerGameSessions.get(multiplayerGameSessionId);
export const setMultiplayerGameSession = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...multiplayerGameSessions: MultiplayerGameSession[]
) => {
  state.multiplayerGameSessions
    .lists(array(fieldNames) as NonEmptyArray<string>)
    .set(...multiplayerGameSessions);
};

export const getGame = (state: SpaceStoreState, gameId: Uuid) =>
  state.games.get(gameId);
export const setGames = (
  state: SpaceStoreState,
  fieldNames: string | string[],
  ...games: Game[]
) => {
  state.games.lists(array(fieldNames) as NonEmptyArray<string>).set(...games);
};

export const getMembership = (
  state: SpaceStoreState,
  classroomId: Uuid,
  userId: Uuid,
) => {
  return state.spaceMemberships
    .map(SpaceMembershipMapType.Recent)
    .get(`${classroomId}_${userId}`);
};

// album
export const getAlbum = (state: SpaceStoreState, gameSetId: Uuid) =>
  state.albums.get(gameSetId);

export const getAlbums = (state: SpaceStoreState, fieldName: string) =>
  state.albums.list(fieldName, null)?.get() ?? [];

export const setAlbum = (state: SpaceStoreState, album: Album) =>
  state.albums.set(album);

export const deleteAlbum = (state: SpaceStoreState, gameSetId: Uuid) =>
  state.albums.delete(gameSetId);

export const setAlbums = (
  state: SpaceStoreState,
  fieldName: AlbumListField | undefined,
  ...albums: Album[]
) => {
  state.albums.list(fieldName ?? AlbumListType.Normal).set(...albums);
};

export const pushAlbums = (
  state: SpaceStoreState,
  fieldName: AlbumListField | undefined,
  ...albums: Album[]
) => {
  state.albums.list(fieldName ?? AlbumListType.Normal).push(...albums);
};

export const setAlbumCursor = (
  state: SpaceStoreState,
  fieldName: AlbumListField | undefined,
  cursor: Cursor,
) => {
  state.albums.list(fieldName ?? AlbumListType.Normal).setCursor(cursor);
};

// performance games
export const getPerformanceGame = (state: SpaceStoreState, gameSetId: Uuid) =>
  state.performanceGames.get(gameSetId);

export const getPerformanceGames = (
  state: SpaceStoreState,
  fieldName: string,
) => state.performanceGames.list(fieldName, null)?.get() ?? [];

export const setPerformanceGames = (
  state: SpaceStoreState,
  fieldName: PerformanceGameListField,
  ...performanceGames: PerformanceGame[]
) => {
  state.performanceGames.list(fieldName).set(...performanceGames);
};

export const pushPerformanceGames = (
  state: SpaceStoreState,
  fieldName: PerformanceGameListField,
  ...performanceGames: PerformanceGame[]
) => {
  state.performanceGames.list(fieldName).push(...performanceGames);
};

export const setPerformanceGameCursor = (
  state: SpaceStoreState,
  fieldName: PerformanceGameListField,
  cursor: Cursor,
) => {
  state.performanceGames.list(fieldName).setCursor(cursor);
};
